import React, { useState, forwardRef } from 'react';
import styles from './Input.module.scss';
import clsx from 'clsx';
import { Eye, EyeOff } from 'lucide-react';
import AppIcon from '../AppIcon/AppIcon';
import Title from '../TitleStyles/title';
import { format, parseISO, isValid } from 'date-fns';

type InputProps = React.InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  type?: string;
  value?: string | number | readonly string[];
  placeholder?: string;
  rightText?: string;
  min?: string | number;
  max?: string | number;
  minLength?: number;
  maxLength?: number;
  showCharCount?: boolean; // for text inputs
  error?: any;
  helpText?: string;
  showPasswordRequirements?: boolean;
  helperPosition?: 'label' | 'input';
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
};

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      type,
      value,
      placeholder,
      rightText,
      min,
      max,
      minLength,
      maxLength,
      showCharCount,
      error,
      helpText,
      showPasswordRequirements,
      helperPosition = 'input',
      onChange,
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === 'password';
    const inputType = isPassword ? (showPassword ? 'text' : 'password') : type ?? 'text';
    const isDateType = type === 'date';

    // Always use the raw value for the input
    const inputValue = value || '';

    // Optionally, show the formatted value below the input for date fields
    let formattedDate = '';
    if (isDateType && inputValue) {
      const parsed = parseISO(inputValue as string);
      if (isValid(parsed)) {
        formattedDate = format(parsed, 'yyyy-MMM-dd');
      }
    }
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e);
    };

    return (
      <div className={styles.inputContainer}>
        {label && <Title subTitle={label} />}
        <div
          className={clsx(styles.input, {
            [styles.hasIcon]: type === 'search',
            [styles.hasError]: !!error,
          })}
        >
          <input
            {...props}
            ref={ref}
            type={inputType}
            min={min}
            max={max}
            minLength={minLength}
            maxLength={maxLength}
            value={inputValue}
            onChange={handleChange}
            placeholder={isDateType ? 'yyyy-MM-dd' : placeholder}
          />
          {type === 'search' && (
            <div className={styles.search}>
              <AppIcon icon='Search' />
            </div>
          )}
          {type === 'password' && (
            <div onClick={() => setShowPassword(!showPassword)} className={styles.passwordToggle}>
              {showPassword ? <EyeOff strokeWidth={2} /> : <Eye strokeWidth={2} />}
            </div>
          )}
          {rightText && <span className={styles.rightText}>{rightText}</span>}
        </div>
        {error && <div className={styles.error}>{error}</div>}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
